import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdDisplay = ({ page, position, className = '' }) => {
  const { user } = useContext(AuthContext);
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const adContainerRef = useRef(null);

  useEffect(() => {
    fetchAd();
  }, [page, position, user]);

  // Handle ad link clicks after content is rendered
  useEffect(() => {
    if (adContainerRef.current && ad) {
      const links = adContainerRef.current.querySelectorAll('a');
      const images = adContainerRef.current.querySelectorAll('img');
      
      // Fix image sources
      images.forEach(img => {
        let src = img.getAttribute('src');
        console.log('AdDisplay: Original image src:', src);
        
        // Remove app domain if it was prepended
        const appDomain = window.location.origin;
        if (src && src.includes(appDomain)) {
          // Extract the actual URL
          const match = src.match(/https?:\/\/(?!bet-monitor-dash)[^"'\s]+/);
          if (match) {
            src = match[0];
            img.src = src;
            console.log('AdDisplay: Fixed image src to:', src);
          }
        }
        
        // Force display
        img.style.display = 'block';
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        img.style.margin = '0 auto';
      });
      
      // Fix links
      links.forEach(link => {
        let href = link.getAttribute('href');
        console.log('AdDisplay: Original link href:', href);
        
        // Remove app domain if it was prepended
        const appDomain = window.location.origin;
        if (href && href.includes(appDomain)) {
          // Extract the actual URL
          const match = href.match(/https?:\/\/(?!bet-monitor-dash)[^"'\s]+/);
          if (match) {
            href = match[0];
            link.href = href;
            console.log('AdDisplay: Fixed link href to:', href);
          }
        }
        
        if (href && href.startsWith('http')) {
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');
          
          // Add click handler
          link.onclick = function(e) {
            e.preventDefault();
            e.stopPropagation();
            console.log('AdDisplay: Navigating to:', href);
            window.open(href, '_blank', 'noopener,noreferrer');
            return false;
          };
        }
      });
    }
  }, [ad]);

  const fetchAd = async () => {
    // Only fetch ads for free users
    if (!user || user.subscription_plan !== 'free') {
      console.log('AdDisplay: User is not free plan, skipping ads');
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('session_token');
    try {
      console.log(`AdDisplay: Fetching ads for page=${page}, position=${position}`);
      const response = await axios.get(`${API}/ads/${page}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log(`AdDisplay: Received ${response.data.length} ads for page ${page}`);
      
      // Find the ad for this specific position
      const matchingAd = response.data.find(a => a.position === position && a.is_active);
      
      if (matchingAd) {
        console.log(`AdDisplay: Found matching ad for position ${position}:`, matchingAd);
      } else {
        console.log(`AdDisplay: No matching ad for position ${position}`);
      }
      
      setAd(matchingAd || null);
    } catch (error) {
      console.error('AdDisplay: Error fetching ad:', error);
      setAd(null);
    } finally {
      setLoading(false);
    }
  };

  // Don't show anything to premium/tipster users
  if (!user || user.subscription_plan !== 'free') {
    return null;
  }

  if (loading) {
    return null;
  }

  if (!ad) {
    return null;
  }

  const renderAdContent = () => {
    if (ad.ad_type === 'html') {
      // Clean the HTML content to remove any app domain prefixes
      let cleanedContent = ad.content;
      
      // Remove any instances where the app domain has been prepended
      const appDomain = window.location.origin;
      cleanedContent = cleanedContent.replace(new RegExp(`${appDomain}/["']?(https?://)`, 'g'), '$1');
      cleanedContent = cleanedContent.replace(new RegExp(`${appDomain}/"(https?://[^"]+)"`, 'g'), '$1');
      
      // Also handle cases where quotes are part of the URL
      cleanedContent = cleanedContent.replace(/href=["']?"(https?:\/\/[^"']+)["']?/g, 'href="$1"');
      cleanedContent = cleanedContent.replace(/src=["']?"(https?:\/\/[^"']+)["']?/g, 'src="$1"');
      
      console.log('AdDisplay: Original content:', ad.content);
      console.log('AdDisplay: Cleaned content:', cleanedContent);
      
      return (
        <div style={{ position: 'relative', width: '100%' }}>
          {/* Sponsored Label */}
          <div style={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: '#9CA3AF',
            fontSize: '10px',
            fontWeight: '600',
            padding: '2px 8px',
            borderRadius: '4px',
            zIndex: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Sponsored
          </div>
          <div 
            ref={adContainerRef}
            className={`ad-container ${className}`}
            style={{
              width: '100%',
              display: 'block',
              textAlign: 'center',
              padding: '15px',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              minHeight: '100px'
            }}
            dangerouslySetInnerHTML={{ __html: cleanedContent }}
          />
        </div>
      );
    } else if (ad.ad_type === 'iframe') {
      return (
        <div style={{ position: 'relative', width: '100%' }}>
          {/* Sponsored Label */}
          <div style={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: '#9CA3AF',
            fontSize: '10px',
            fontWeight: '600',
            padding: '2px 8px',
            borderRadius: '4px',
            zIndex: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Sponsored
          </div>
          <div className={`ad-container ${className}`} style={{ width: '100%' }}>
            <iframe
              src={ad.content}
              width="100%"
              height="90"
              frameBorder="0"
              scrolling="no"
              title={`Ad ${position}`}
              className="rounded"
              sandbox="allow-scripts allow-same-origin allow-popups"
            />
          </div>
        </div>
      );
    } else if (ad.ad_type === 'script') {
      return (
        <div style={{ position: 'relative', width: '100%' }}>
          {/* Sponsored Label */}
          <div style={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: '#9CA3AF',
            fontSize: '10px',
            fontWeight: '600',
            padding: '2px 8px',
            borderRadius: '4px',
            zIndex: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Sponsored
          </div>
          <div 
            className={`ad-container ${className}`}
            style={{ width: '100%' }}
            dangerouslySetInnerHTML={{ __html: ad.content }}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`my-4 ${className}`} style={{ width: '100%', display: 'block' }}>
      {renderAdContent()}
    </div>
  );
};

export default AdDisplay;
